/**
 * Card 2508 — a board whose declaration names VIEWS: tiles and tabs, one per declared view, over a
 * table of the declared columns (the approved prototype's screen 1, "Clients").
 *
 * <p>Everything a staffer reads here is DECLARED: the board's label (`status_view.board_label`), each
 * tab's words and count (`status_view.views[]`), and each column (`status_view.list_fields`). The
 * page sends back only a declared view NAME (`?view=`); which rows a view holds is decided by the
 * engine against the declaration, never by a filter the page builds. A view named in the address
 * that the board does not declare is never sent: the page shows the first declared view instead.</p>
 *
 * <p>A row opens the record's own page under this board (`/boards/<workflow>/<id>`), where the
 * declared actions live; a row carries no action of its own.</p>
 *
 * <p>Design tokens only — no raw hex (test/polish/hex-fence.test.ts).</p>
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  Icon,
  LastUpdated,
  PageHeader,
  Table,
  type Column,
} from "@ardept/design-system";
import { ApiError } from "../api/fetcher.js";
import { useClientTenant, useJobStatusFiles, useJobStatusOutstanding } from "../api/portalQueries.js";
import type { JobStatusFile, JobStatusFilesResponse, JobStatusView } from "../api/types.js";
import { importLabel, importRouteFor, type BoardEntry } from "../nav/familyRegistry.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import { NO_CLIENT_NAME, ownerLabel } from "./FilesBoardPage.js";
import { fileDisplayName } from "./FileDetailPage.js";
import { groupByFile, type FileGroup } from "./OutstandingItemsPage.js";
import { shortDay } from "./dates.js";
import { FieldCell, columnsFor, type BoardColumn } from "./boardFields.js";
import "./FilesBoardPage.css";
import "./ViewsBoardPage.css";

/** The query parameter the selected view lives in, so a tab can be bookmarked and shared. */
export const VIEW_PARAM = "view";

/**
 * #2507 — the query parameter (and its one value) for the Still owed tab. A separate parameter from
 * `view`, so it can never collide with a name a declaration chooses for a view.
 */
export const LIST_PARAM = "list";
export const OWED_LIST = "owed";

// ── #2507: the Still owed tab ───────────────────────────────────────────────────────────────
//
// Not a declared view: it reads the firm-wide chase list (`/job-status/outstanding`), which now
// carries approved files' owed items too (tax references, UTRs, personal codes; never a staff
// decision). One row per FILE, oldest owed first, as the engine orders it. The reminder itself is
// sent from the file's own page, where the engine says whether this viewer may send it: a chase
// row carries no action reference.

interface OwedRow {
  id: string;
  group: FileGroup;
}

/** "The client, a party" — who is being chased on a file, each once, in the order first owed. */
export function owedByWords(group: FileGroup): string {
  const words: string[] = [];
  for (const row of group.items) {
    const who = ownerLabel(row.item.owner);
    if (who === null) continue;
    const word = who[0]!.toUpperCase() + who.slice(1);
    if (!words.includes(word)) words.push(word);
  }
  return words.join(", ");
}

/** What a file still owes, in its own words: each item's subject, else its declared kind. */
export function owedItemsWords(group: FileGroup): string {
  return group.items.map((row) => row.item.subject ?? row.item.kind).join(", ");
}

function owedColumns(now: number): Column<OwedRow>[] {
  return [
    {
      key: "file",
      label: "Name",
      render: (row) => {
        const name = fileDisplayName(row.group.clientName);
        return (
          <Link className={`fb-file${name.named ? "" : " fb-file--unnamed"}`} to={`/onboarding/${row.group.jobId}`}>
            {name.text}
          </Link>
        );
      },
    },
    { key: "stage", label: "Stage", render: (row) => <span className="fb-muted">{row.group.stageLabel}</span> },
    { key: "items", label: "Still owed", render: (row) => owedItemsWords(row.group) },
    { key: "by", label: "By", render: (row) => <span className="fb-muted">{owedByWords(row.group)}</span> },
    {
      key: "since",
      label: "Owed since",
      render: (row) => <span className="vb-day">{shortDay(row.group.items[0]?.item.since, now) ?? NOT_RECORDED_WORDS}</span>,
    },
    {
      key: "open",
      label: "",
      render: (row) => (
        <Link className="btn btn--ghost" to={`/onboarding/${row.group.jobId}`}>
          Open to send a reminder
        </Link>
      ),
    },
  ];
}

const NOT_RECORDED_WORDS = "Not recorded";

/** A count as a person reads it: "1,163". */
export function countLabel(count: number): string {
  return count.toLocaleString("en-GB");
}

/** The view the address asks for, if the board declares it; else the first declared view. */
export function selectedView(views: readonly JobStatusView[], requested: string | null): JobStatusView | undefined {
  return views.find((view) => view.name === requested) ?? views[0];
}

/** Does a row match the search box? Its name, or any of its text columns (a client code). */
export function rowMatches(file: JobStatusFile, needle: string): boolean {
  if (needle === "") return true;
  if ((file.client_name ?? "").toLowerCase().includes(needle)) return true;
  return Object.values(file.fields ?? {}).some(
    (field) => field.type === "text" && (field.value ?? "").toLowerCase().includes(needle),
  );
}

interface Row {
  id: string;
  file: JobStatusFile;
}

function RecordLink({ file, base }: { file: JobStatusFile; base: string }): JSX.Element {
  const named = file.client_name !== null && file.client_name.trim() !== "";
  return (
    <Link className={`fb-file${named ? "" : " fb-file--unnamed"}`} to={`${base}/${file.job_id}`}>
      {named ? file.client_name : NO_CLIENT_NAME}
    </Link>
  );
}

function tableColumns(columns: readonly BoardColumn[], base: string, now: number): Column<Row>[] {
  return [
    { key: "record", label: "Name", render: (row) => <RecordLink file={row.file} base={base} /> },
    ...columns.map(
      (column): Column<Row> => ({
        key: `field:${column.name}`,
        label: column.label,
        render: (row) => <FieldCell fields={row.file.fields} column={column} now={now} />,
      }),
    ),
  ];
}

/** Does a still-owed file match the search box? Its name. */
function needleMatches(group: FileGroup, needle: string): boolean {
  return needle === "" || (group.clientName ?? "").toLowerCase().includes(needle);
}

/** The Still owed tab's panel: one row per file still owed something, or why there is none. */
function StillOwedPanel({
  rows,
  loading,
  failed,
  searching,
  now,
}: {
  rows: OwedRow[];
  loading: boolean;
  failed: boolean;
  searching: boolean;
  now: number;
}): JSX.Element {
  if (failed) {
    return (
      <div className="state state--error" role="status">
        <h2 className="state__title">We could not load what is still owed</h2>
        <p className="state__body">Please try again in a moment. If it keeps happening, contact your AR Dept contact.</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="fb-loading vb-pad" aria-busy="true" aria-live="polite">
        <span className="fb-loading__label">Loading what is still owed…</span>
        <div className="skeleton" />
      </div>
    );
  }
  return (
    <>
      <p className="vb-note">
        Files where something is still owed, including approved ones. Open a file to send a reminder.
      </p>
      <Table<OwedRow>
        columns={owedColumns(now)}
        rows={rows}
        caption="Still owed"
        emptyState={
          <EmptyState
            iconName="check-circle-2"
            title="Nothing is still owed"
            body={searching ? "No file still owed something matches your search." : "No file is waiting on anything right now."}
          />
        }
      />
    </>
  );
}

/**
 * The page. `overview` is the board's unfiltered first page — already read to decide this board
 * has views — whose `views[]` carry every tab's count.
 */
export function ViewsBoardPage({
  board,
  overview,
  refreshOverview,
}: {
  board: BoardEntry;
  overview: JobStatusFilesResponse;
  refreshOverview: () => void;
}): JSX.Element {
  const identity = useClientTenant();
  usePageTitle(board.label);

  const views = overview.views;
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = selectedView(views, searchParams.get(VIEW_PARAM));
  const showOwed = searchParams.get(LIST_PARAM) === OWED_LIST;
  const [query, setQuery] = useState("");

  // #2507: the chase list, for the Still owed tile and tab.
  const chase = useJobStatusOutstanding(identity.tenantId);
  const owedGroups = useMemo(() => groupByFile(chase.data?.items ?? []), [chase.data]);

  const list = useJobStatusFiles(identity.tenantId, {
    workflowId: board.workflowId,
    view: selected?.name ?? null,
    enabled: selected !== undefined,
  });
  const data = list.data;
  const now = useMemo(() => Date.now(), [data]);

  const choose = (name: string): void => {
    const params = new URLSearchParams(searchParams);
    params.set(VIEW_PARAM, name);
    params.delete(LIST_PARAM);
    setSearchParams(params, { replace: true });
    setQuery("");
  };
  const chooseOwed = (): void => {
    const params = new URLSearchParams(searchParams);
    params.set(LIST_PARAM, OWED_LIST);
    setSearchParams(params, { replace: true });
    setQuery("");
  };
  const owedCount = chase.data === undefined ? null : owedGroups.length;
  const owedRows: OwedRow[] = owedGroups
    .filter((group) => needleMatches(group, query.trim().toLowerCase()))
    .map((group) => ({ id: group.jobId, group }));

  const needle = query.trim().toLowerCase();
  const rows: Row[] = (data?.files ?? [])
    .filter((file) => rowMatches(file, needle))
    .map((file) => ({ id: file.job_id, file }));
  const columns = columnsFor(data?.files ?? []);

  const listUnresolved = data === undefined && list.error === null;
  const refusedView = list.error instanceof ApiError && list.error.status === 400;

  return (
    <div className="views-board files-board" data-testid="views-board">
      <PageHeader
        title={board.label}
        subtitle="What is due, what is running out and what is still owed."
        actions={
          <>
            <label className="fb-search">
              <Icon name="search" size={16} aria-hidden="true" />
              <input
                type="search"
                className="fb-search__input"
                placeholder="Find by name or code"
                aria-label="Find by name or code"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <Button
              variant="ghost"
              onClick={() => {
                refreshOverview();
                void list.refetch();
                void chase.refetch();
              }}
              disabled={list.isFetching}
            >
              {list.isFetching ? "Refreshing…" : "Refresh"}
            </Button>
            {/* #2508: the board read says whether this declaration takes an import (`has_import`). */}
            {overview.has_import === true && importRouteFor(board) !== null && (
              <Link className="btn btn--ghost" to={importRouteFor(board)!}>
                {importLabel(board)}
              </Link>
            )}
          </>
        }
      />

      <LastUpdated timestamp={new Date((data ?? overview).as_of)} />

      <section className="vb-tiles" aria-label="Summary">
        {views.map((view) => (
          <button
            key={view.name}
            type="button"
            className={`vb-tile${!showOwed && view.name === selected?.name ? " is-on" : ""}`}
            aria-pressed={!showOwed && view.name === selected?.name}
            onClick={() => choose(view.name)}
          >
            <span className="vb-tile__n num">{countLabel(view.count)}</span>
            <span className="vb-tile__l">{view.label}</span>
          </button>
        ))}
        <button
          type="button"
          className={`vb-tile${showOwed ? " is-on" : ""}`}
          aria-pressed={showOwed}
          onClick={chooseOwed}
        >
          <span className="vb-tile__n num">{owedCount === null ? "…" : countLabel(owedCount)}</span>
          <span className="vb-tile__l">Files with something still owed</span>
        </button>
      </section>

      <section className="vb-card" aria-label={`${board.label} list`}>
        <div className="vb-tabs" role="tablist" aria-label="Lists">
          {views.map((view) => (
            <button
              key={view.name}
              type="button"
              role="tab"
              id={`vb-tab-${view.name}`}
              aria-selected={!showOwed && view.name === selected?.name}
              aria-controls="vb-panel"
              className={`vb-tab${!showOwed && view.name === selected?.name ? " is-on" : ""}`}
              onClick={() => choose(view.name)}
            >
              {view.label}
              <span className="vb-tab__c num">{countLabel(view.count)}</span>
            </button>
          ))}
          <button
            type="button"
            role="tab"
            id="vb-tab-owed"
            aria-selected={showOwed}
            aria-controls="vb-panel"
            className={`vb-tab${showOwed ? " is-on" : ""}`}
            onClick={chooseOwed}
          >
            Still owed
            {owedCount !== null && <span className="vb-tab__c num">{countLabel(owedCount)}</span>}
          </button>
        </div>

        <div
          id="vb-panel"
          role="tabpanel"
          aria-labelledby={showOwed ? "vb-tab-owed" : selected === undefined ? undefined : `vb-tab-${selected.name}`}
        >
          {showOwed && (
            <StillOwedPanel
              rows={owedRows}
              loading={chase.data === undefined && chase.error === null}
              failed={chase.error !== null}
              searching={query.trim() !== ""}
              now={now}
            />
          )}

          {!showOwed && list.error !== null && (
            <div className="state state--error" role="status">
              <h2 className="state__title">
                {refusedView ? "That list is not on this board" : "We could not load this list"}
              </h2>
              <p className="state__body">
                {refusedView
                  ? "Choose one of the lists above."
                  : "Please try again in a moment. If it keeps happening, contact your AR Dept contact."}
              </p>
            </div>
          )}

          {!showOwed && list.error === null && listUnresolved && (
            <div className="fb-loading vb-pad" aria-busy="true" aria-live="polite">
              <span className="fb-loading__label">Loading this list…</span>
              <div className="skeleton" style={{ width: "60%" }} />
              <div className="skeleton" />
            </div>
          )}

          {!showOwed && list.error === null && data !== undefined && (
            <Table<Row>
              columns={tableColumns(columns, board.route, now)}
              rows={rows}
              caption={selected?.label ?? board.label}
              emptyState={
                <EmptyState
                  iconName="search-x"
                  title="Nothing to show"
                  body={needle !== "" ? "Nobody on this list matches your search." : "Nobody is on this list right now."}
                />
              }
              footer={
                <div className="vb-foot">
                  <span>
                    Showing {countLabel(data.files.length)}
                    {selected !== undefined ? ` of ${countLabel(Math.max(selected.count, data.files.length))}` : ""}
                    {needle !== "" ? `, ${countLabel(rows.length)} matching your search` : ""}
                  </span>
                  {list.hasMore && (
                    <Button variant="ghost" onClick={list.showMore} disabled={list.isLoadingMore}>
                      {list.isLoadingMore ? "Loading more…" : "Show more"}
                    </Button>
                  )}
                </div>
              }
            />
          )}
        </div>
      </section>
    </div>
  );
}

export default ViewsBoardPage;
